/**
 * Recharge Subscription Widget - Per-Serving Price Display
 * Adds "$XX.XX/serving" before Recharge price elements when product variants contain "*-serving"
 */

(function () {
  'use strict';

  class RechargeServingPrices {
    constructor() {
      this.initialized = false;
      this.observer = null;
      this.rechargeContainer = null;
      this.currentProduct = null;
      this.currentVariant = null;
      this.servingPriceElements = new Set();

      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      console.log('[RechargeServingPrices] Setup starting...');

      // Only run on product pages
      if (!this.isProductPage()) {
        console.log('[RechargeServingPrices] Not a product page, exiting');
        return;
      }

      console.log('[RechargeServingPrices] Product page detected');

      this.loadProductData();
      this.findRechargeContainer();
      this.setupVariantChangeListener();
      this.startObserving();

      this.initialized = true;
      console.log(
        '[RechargeServingPrices] Setup completed, initialized:',
        this.initialized
      );
    }

    isProductPage() {
      return document.querySelector('[data-section-type="product"]') !== null;
    }

    loadProductData() {
      try {
        console.log('[RechargeServingPrices] Loading product data...');
        const productJson = document.querySelector('[data-product-json]');

        if (productJson && productJson.textContent) {
          this.currentProduct = JSON.parse(productJson.textContent);

          // Check if there's a variant parameter in the URL
          const urlParams = new URLSearchParams(window.location.search);
          const variantIdFromUrl = urlParams.get('variant');

          if (variantIdFromUrl && this.currentProduct?.variants) {
            // Use variant from URL if available
            this.currentVariant = this.currentProduct.variants.find(
              (v) => v.id === parseInt(variantIdFromUrl, 10)
            );
            console.log(
              '[RechargeServingPrices] Using variant from URL:',
              variantIdFromUrl
            );
          }

          // Fallback to default variant selection
          if (!this.currentVariant) {
            this.currentVariant =
              this.currentProduct?.selected_or_first_available_variant ||
              this.currentProduct?.variants?.[0];
          }

          console.log(
            '[RechargeServingPrices] Product loaded:',
            this.currentProduct?.title
          );
          console.log(
            '[RechargeServingPrices] Current variant:',
            this.currentVariant?.title
          );
        } else {
          console.warn('[RechargeServingPrices] No product JSON found');
        }
      } catch (error) {
        console.warn(
          '[RechargeServingPrices] Could not load product data',
          error
        );
      }
    }

    setupVariantChangeListener() {
      // Listen for theme variant change event
      document.addEventListener(
        'theme:variant:change',
        this.handleVariantChange.bind(this)
      );
    }

    handleVariantChange(event) {
      // Get the new variant from the event detail
      if (event.detail && event.detail.variant) {
        this.currentVariant = event.detail.variant;

        // Update serving prices with new variant
        setTimeout(() => {
          this.updateServingPrices();
        }, 100);
      }
    }

    findRechargeContainer() {
      console.log('[RechargeServingPrices] Looking for Recharge container...');

      // Look for Recharge custom elements and app blocks
      const rechargeSelectors = [
        'recharge-subscription-widget',
        '[id*="shopify-app-block"][id*="recharge"]',
        '[id*="recharge"]',
      ];

      for (const selector of rechargeSelectors) {
        const elements = document.querySelectorAll(selector);
        console.log(
          '[RechargeServingPrices] Found elements for',
          selector + ':',
          elements.length
        );

        for (const element of elements) {
          console.log(
            '[RechargeServingPrices] Checking element:',
            element.tagName,
            element.id || 'no-id'
          );
          const shadowRoot = this.findShadowRoot(element);
          if (shadowRoot) {
            this.rechargeContainer = shadowRoot;
            console.log(
              '[RechargeServingPrices] Shadow root found in:',
              element.tagName
            );
            return;
          }
        }
      }

      if (!this.rechargeContainer) {
        console.warn('[RechargeServingPrices] No Recharge shadow root found');
      }
    }

    findShadowRoot(element) {
      // Check if element has shadow root
      if (element.shadowRoot) {
        return element.shadowRoot;
      }

      // Check child elements for shadow roots
      for (const child of element.children) {
        if (child.shadowRoot) {
          return child.shadowRoot;
        }

        // Recursively check nested elements
        const nestedShadowRoot = this.findShadowRoot(child);
        if (nestedShadowRoot) {
          return nestedShadowRoot;
        }
      }

      return null;
    }

    startObserving() {
      console.log('[RechargeServingPrices] Starting to observe...');

      // Start observing the product form area for the Recharge widget to appear
      this.observeForRechargeWidget();
    }

    observeForRechargeWidget() {
      console.log(
        '[RechargeServingPrices] Setting up widget detection observer...'
      );

      // Look specifically for the recharge widget container
      const rechargeWidget = document.querySelector(
        'recharge-subscription-widget'
      );

      if (rechargeWidget) {
        console.log(
          '[RechargeServingPrices] Found existing recharge-subscription-widget'
        );
        this.checkForExistingWidget();
        return;
      }

      // If not found, observe document for the widget to appear
      console.log(
        '[RechargeServingPrices] Observing document for recharge-subscription-widget to appear'
      );

      this.widgetObserver = new MutationObserver((mutations) => {
        this.handleWidgetMutations(mutations);
      });

      // Only observe the document root since we're looking for the specific widget
      this.widgetObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Also check if widget is already present (fallback)
      this.checkForExistingWidget();
    }

    handleWidgetMutations(mutations) {
      let foundWidget = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check specifically for recharge-subscription-widget
              if (
                node.tagName === 'RECHARGE-SUBSCRIPTION-WIDGET' ||
                (node.querySelector &&
                  node.querySelector('recharge-subscription-widget'))
              ) {
                console.log(
                  '[RechargeServingPrices] recharge-subscription-widget detected in DOM!'
                );
                foundWidget = true;
                break;
              }
            }
          }
        }
      }

      if (foundWidget) {
        // Give the widget a moment to fully initialize
        setTimeout(() => {
          this.findRechargeContainer();
          if (this.rechargeContainer) {
            console.log(
              '[RechargeServingPrices] Switching to price element observation'
            );
            this.stopWidgetObserver();
            this.observePriceElements();
          } else {
            console.log(
              '[RechargeServingPrices] Widget found but no shadow root yet, continuing to observe...'
            );
          }
        }, 500);
      }
    }

    isRechargeWidget(element) {
      return (
        element.tagName === 'RECHARGE-SUBSCRIPTION-WIDGET' ||
        (element.id && element.id.includes('recharge'))
      );
    }

    checkForExistingWidget() {
      console.log(
        '[RechargeServingPrices] Checking for existing Recharge widget...'
      );

      this.findRechargeContainer();
      if (this.rechargeContainer) {
        console.log(
          '[RechargeServingPrices] Found existing widget, switching to price observation'
        );
        this.stopWidgetObserver();
        this.observePriceElements();
      } else {
        console.log(
          '[RechargeServingPrices] No existing widget found, will wait for it to load'
        );
      }
    }

    stopWidgetObserver() {
      if (this.widgetObserver) {
        this.widgetObserver.disconnect();
        this.widgetObserver = null;
        console.log('[RechargeServingPrices] Widget observer stopped');
      }
    }

    observePriceElements() {
      console.log(
        '[RechargeServingPrices] Setting up price element observer...'
      );

      // Give the Recharge widget time to fully render before processing
      setTimeout(() => {
        this.processExistingPriceElements();
      }, 1000);

      // Also try again after a longer delay in case the widget loads slowly
      setTimeout(() => {
        console.log(
          '[RechargeServingPrices] Delayed check for price elements...'
        );
        if (this.findPriceElements().length > 0) {
          this.processExistingPriceElements();
        }
      }, 3000);

      // Set up mutation observer to watch for price changes
      this.setupMutationObserver();
    }

    setupMutationObserver() {
      // Phase 1: Observe recharge-subscription-widget until prices are loaded
      const rechargeWidget = document.querySelector(
        'recharge-subscription-widget'
      );
      const observeTarget = this.rechargeContainer || rechargeWidget;

      if (!observeTarget) {
        console.warn(
          '[RechargeServingPrices] No recharge widget found to observe'
        );
        return;
      }

      console.log(
        '[RechargeServingPrices] Observing widget for price loading...'
      );

      this.widgetLoadObserver = new MutationObserver(() => {
        // Look for rc-purchase-option__price to appear (indicates widget is loaded)
        const priceElement = observeTarget.querySelector(
          '[part="rc-purchase-option__price"]'
        );

        if (priceElement) {
          console.log(
            '[RechargeServingPrices] Widget loaded, setting up price observers'
          );

          // Stop observing the widget
          this.widgetLoadObserver.disconnect();
          this.widgetLoadObserver = null;

          // Start Phase 2: Observe specific price elements
          this.setupPriceElementObserver();
        }
      });

      this.widgetLoadObserver.observe(observeTarget, {
        childList: true,
        subtree: true,
      });

      // Check if price already exists (widget might already be loaded)
      const existingPrice = observeTarget.querySelector(
        '[part="rc-purchase-option__price"]'
      );
      if (existingPrice) {
        console.log(
          '[RechargeServingPrices] Widget already loaded, setting up observers'
        );
        this.widgetLoadObserver.disconnect();
        this.widgetLoadObserver = null;
        this.setupPriceElementObserver();
      }
    }

    setupPriceElementObserver() {
      // Find the 3 specific price elements to observe
      const observeTarget =
        this.rechargeContainer ||
        document.querySelector('recharge-subscription-widget');
      const priceElements = [
        observeTarget.querySelector('[part="rc-purchase-option__price"]'),
        observeTarget.querySelector(
          '[part="rc-purchase-option__discounted-price"]'
        ),
        observeTarget.querySelector('[part="rc-purchase-option__unit-price"]'),
      ].filter((el) => el !== null);

      if (priceElements.length === 0) {
        console.warn('[RechargeServingPrices] No price elements found');
        return;
      }

      console.log(
        '[RechargeServingPrices] Observing',
        priceElements.length,
        'price elements'
      );

      // Create observer for price element changes
      this.observer = new MutationObserver(() => {
        if (this.isUpdating) return; // Prevent recursive updates

        clearTimeout(this.updateTimeout);
        this.updateTimeout = setTimeout(() => {
          this.updateServingPrices();
        }, 300);
      });

      // Observe each price element individually
      priceElements.forEach((element) => {
        this.observer.observe(element, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      });
    }

    processExistingPriceElements() {
      this.updateServingPrices();
    }

    updateServingPrices() {
      // Prevent recursive updates
      if (this.isUpdating) return;

      this.isUpdating = true;

      try {
        if (!this.shouldShowServingPrices()) {
          this.removeAllServingPrices();
          return;
        }

        const servingCount = this.extractServingCount();

        if (!servingCount || servingCount <= 0) {
          this.removeAllServingPrices();
          return;
        }

        // Remove existing serving prices first
        this.removeAllServingPrices();

        console.log(
          '[RechargeServingPrices] Updating serving prices for',
          servingCount + '-serving'
        );

        // Add new serving prices to containers
        const pricesContainers = this.findPricesContainers();
        pricesContainers.forEach((container) => {
          this.addServingPriceToContainer(container, servingCount);
        });
      } finally {
        this.isUpdating = false;
      }
    }

    findPriceElements() {
      const selectors = [
        '[part="rc-purchase-option__price"]', // Individual price element
        '[part="rc-purchase-option__unit-price"]', // Unit price element
        '[part="rc-purchase-option__discounted-price"]', // Discounted price element
      ];

      const elements = [];
      const searchRoot = this.rechargeContainer || document;

      selectors.forEach((selector) => {
        const found = searchRoot.querySelectorAll(selector);
        elements.push(...found);
      });
      return elements;
    }

    findPricesContainers() {
      // Find the containers that hold the price elements
      const searchRoot = this.rechargeContainer || document;
      return searchRoot.querySelectorAll('[part="rc-purchase-option__prices"]');
    }

    isPriceElement(element) {
      if (!element.getAttribute) return false;

      const part = element.getAttribute('part');
      return (
        part &&
        (part.includes('rc-purchase-option__prices') ||
          part.includes('rc-purchase-option__discounted-price'))
      );
    }

    shouldShowServingPrices() {
      return this.extractServingCount() > 0;
    }

    extractServingCount() {
      if (!this.currentVariant?.title && !this.currentProduct?.title) {
        return 0;
      }

      const title =
        this.currentVariant?.title || this.currentProduct?.title || '';
      const servingMatch = title.match(/(\d+)-serving/i);
      const servingCount = servingMatch ? parseInt(servingMatch[1], 10) : 0;

      return servingCount;
    }

    isInSubscriptionSection(element) {
      // Check if this price element is within the subscription section
      let parent = element.parentNode;
      let depth = 0;

      while (parent && depth < 10) {
        const parentPart =
          (parent.getAttribute && parent.getAttribute('part')) || '';

        // Check for subscription section indicators
        if (
          parentPart === 'rc-purchase-option__subscription' ||
          parentPart.includes('subscription')
        ) {
          console.log(
            '[RechargeServingPrices] Found subscription section for price element'
          );
          return true;
        }

        // Check for one-time section indicators - exclude these
        if (
          parentPart === 'rc-purchase-option__onetime' ||
          parentPart.includes('onetime')
        ) {
          console.log(
            '[RechargeServingPrices] Found one-time section, excluding'
          );
          return false;
        }

        parent = parent.parentNode;
        depth++;
      }

      // For subscription-specific price parts, include by default
      const subscriptionParts = [
        'rc-purchase-option__discounted-price',
        'rc-purchase-option__original-price',
        'rc-purchase-option__unit-price',
      ];

      const elementPart = element.getAttribute('part');
      if (subscriptionParts.includes(elementPart)) {
        console.log(
          '[RechargeServingPrices] Element is subscription-specific part, including:',
          elementPart
        );
        return true;
      }

      console.log(
        '[RechargeServingPrices] Could not determine section, excluding by default:',
        elementPart
      );
      return false;
    }

    addServingPriceToContainer(container, servingCount) {
      // Find price elements within this container to extract the price
      const priceSelectors = [
        '[part="rc-purchase-option__price"]',
        '[part="rc-purchase-option__unit-price"]',
        '[part="rc-purchase-option__discounted-price"]',
      ];

      let priceElement = null;
      let usedSelector = '';

      // Find the first available price element
      for (const selector of priceSelectors) {
        priceElement = container.querySelector(selector);
        if (priceElement) {
          usedSelector = selector;
          break;
        }
      }

      if (!priceElement) {
        return;
      }

      const price = this.extractPriceFromElement(priceElement);

      if (!price || price <= 0) {
        return;
      }

      // Calculate per-serving price using this element's price
      const perServingPrice = (price / servingCount).toFixed(2);

      console.log(
        '[RechargeServingPrices] Adding serving price $' +
          perServingPrice +
          ' using',
        usedSelector
      );

      // Create serving price element
      const elementId = this.getUniqueElementId(container);
      const servingPriceElement = this.createServingPriceElement(
        perServingPrice,
        elementId,
        'rc-purchase-option__prices'
      );

      // Append serving price as the last child of the container
      container.appendChild(servingPriceElement);
      this.servingPriceElements.add(servingPriceElement);
    }

    updateExistingServingPrice(
      servingPriceElement,
      priceElement,
      perServingPrice
    ) {
      console.log(
        '[RechargeServingPrices] Updating serving price to:',
        perServingPrice,
        'for',
        priceElement.getAttribute('part')
      );
      const priceSpan = servingPriceElement.querySelector(
        '.serving-price__amount'
      );
      if (priceSpan) {
        priceSpan.textContent = `$${perServingPrice}`;
      }
    }

    getUniqueElementId(element) {
      const part = element.getAttribute('part') || 'unknown';
      const index = Array.from(element.parentNode?.children || []).indexOf(
        element
      );
      return `${part}-${index}`;
    }

    findExistingServingPrice(priceElement, elementId) {
      // Look for serving price element that belongs to this specific price element
      const siblings = Array.from(priceElement.parentNode?.children || []);
      const elementIndex = siblings.indexOf(priceElement);

      // Check the previous sibling (where we insert serving prices)
      if (elementIndex > 0) {
        const previousSibling = siblings[elementIndex - 1];
        if (
          previousSibling &&
          previousSibling.classList.contains('serving-price') &&
          previousSibling.getAttribute('data-element-id') === elementId
        ) {
          return previousSibling;
        }
      }

      return null;
    }

    extractPriceFromElement(element) {
      // Get all text content from the element and its children
      const text = element.textContent || element.innerText || '';

      // Try multiple regex patterns to extract price
      const patterns = [
        /\$([0-9,]+\.?\d*)/, // $29.99
        /([0-9,]+\.?\d*)\s*\$/, // 29.99$
        /([0-9,]+\.?\d+)/, // 29.99 (without $)
        /USD\s*([0-9,]+\.?\d*)/i, // USD 29.99
        /([0-9,]+\.?\d*)\s*USD/i, // 29.99 USD
      ];

      for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match) {
          const price = parseFloat(match[1].replace(/,/g, ''));
          if (price > 0) {
            return price;
          }
        }
      }

      return 0;
    }
    // remove the serving price element during promo 03/18/2026 
    // createServingPriceElement(perServingPrice, elementId, sourcePartAttribute) {
    //   const element = document.createElement('div');
    //   element.className = 'serving-price';
    //   element.setAttribute('part', 'serving-price');
    //   element.setAttribute('data-element-id', elementId);
    //   element.setAttribute('data-source-part', sourcePartAttribute); // Track which price element this belongs to
    //   element.innerHTML = `
    //     <span class="serving-price__amount" part="serving-price-amount">$${perServingPrice}</span>
    //     <span class="serving-price__label" part="serving-price-label">/serving</span>
    //   `;

    //   return element;
    // }

    removeAllServingPrices() {
      this.servingPriceElements.forEach((element) => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
      this.servingPriceElements.clear();

      // Also remove any orphaned serving prices
      const orphanedElements = document.querySelectorAll('.serving-price');
      orphanedElements.forEach((element) => element.remove());
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.stopWidgetObserver();
      this.removeAllServingPrices();
      clearTimeout(this.updateTimeout);

      // Clean up variant change listener
      document.removeEventListener(
        'theme:variant:change',
        this.handleVariantChange
      );
    }
  }

  // Initialize when page loads
  let servingPricesInstance;

  function initRechargeServingPrices() {
    if (servingPricesInstance) {
      servingPricesInstance.destroy();
    }

    servingPricesInstance = new RechargeServingPrices();
  }

  // Initialize on page load
  initRechargeServingPrices();

  // Reinitialize on theme section load (for theme editor)
  document.addEventListener('shopify:section:load', initRechargeServingPrices);
})();
